import { useEffect, useState } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import { ConfigProvider, Layout, theme } from "antd";
import { useSelector } from "react-redux";
import "@ant-design/v5-patch-for-react-19";

// import actions
import { loadUser } from "./actions/auth";
import { LOGOUT } from "./actions/constants";

// import utils
import setAuthToken from "./untils/setAuthToken";

// import layout
import HeaderSection from "./pages/layout/HeaderSection";
import SidebarSection from "./pages/layout/SidebarSection";
import MainSection from "./pages/layout/MainSection";
import ChatSection from "./pages/layout/ChatSection";
import BrandCanvas from "./components/BrandCanvas";

// get store
import store from "./store";

// import style
import "./style/layout.scss";

const App = () => {
  const themeMode = useSelector((state) => state.themeMode);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sidebarCollapsed") === "true",
  );

  const handleSidebarCollapse = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  useEffect(() => {
    if (localStorage.token) {
      setAuthToken(localStorage.token);
      store.dispatch(loadUser());
    }

    window.addEventListener("storage", () => {
      if (!localStorage.token) store.dispatch({ type: LOGOUT });
    });
  }, []);

  useEffect(() => {
    document.documentElement.dataset.appTheme = themeMode.themeMode
      ? "dark"
      : "light";

    return () => {
      delete document.documentElement.dataset.appTheme;
    };
  }, [themeMode.themeMode]);

  return (
    <ConfigProvider
      theme={{
        algorithm: themeMode.themeMode
          ? theme.darkAlgorithm
          : theme.defaultAlgorithm,
        token: {
          colorPrimary: "#087b85",
          colorInfo: "#256bfe",
          colorSuccess: "#13a876",
          colorWarning: "#b87a12",
          colorError: "#c44747",
          colorLink: "#087b85",
          borderRadius: 8,
          borderRadiusLG: 12,
          borderRadiusSM: 6,
          controlHeight: 40,
          fontFamily:
            'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          boxShadowSecondary: "0 20px 60px rgba(16, 33, 43, 0.12)",
          colorBgLayout: themeMode.themeMode ? "#0e1b22" : "#f4fbfc",
          colorBgContainer: themeMode.themeMode ? "#122630" : "#ffffff",
          colorBgElevated: themeMode.themeMode ? "#10232c" : "#ffffff",
          colorBgSpotlight: themeMode.themeMode ? "#19313c" : "#123a45",
          colorBorder: themeMode.themeMode ? "#2d4651" : "#d8e8ec",
          colorText: themeMode.themeMode ? "#e8f2f5" : "#10212b",
          colorTextSecondary: themeMode.themeMode ? "#a7bbc4" : "#607783",
        },
        components: {
          Layout: {
            headerBg: themeMode.themeMode ? "#122630" : "#ffffff",
            bodyBg: themeMode.themeMode ? "#0e1b22" : "#f4fbfc",
          },
          Button: {
            primaryShadow: "0 10px 24px rgba(8, 123, 133, .22)",
            fontWeight: 600,
          },
          Card: { paddingLG: 22 },
          Table: {
            headerBg: themeMode.themeMode ? "#13262f" : "#f4fbfc",
            headerColor: themeMode.themeMode ? "#a7bbc4" : "#607783",
            rowHoverBg: themeMode.themeMode ? "#19313c" : "#f7fcfd",
            borderColor: themeMode.themeMode ? "#2d4651" : "#e5f0f2",
          },
          Modal: {
            titleFontSize: 19,
            contentBg: themeMode.themeMode ? "#122630" : "#fff",
            headerBg: themeMode.themeMode ? "#10232c" : "#fff",
          },
          Menu: { itemBorderRadius: 9, subMenuItemBorderRadius: 9 },
          Dropdown: { paddingBlock: 6 },
          Tabs: { itemSelectedColor: "#087b85", inkBarColor: "#087b85" },
          Segmented: { itemSelectedBg: "#087b85", itemSelectedColor: "#fff" },
          Pagination: { itemActiveBg: "#087b85", itemActiveColor: "#fff" },
        },
      }}
    >
      <Router>
        <div
          className={`main-section ${themeMode.themeMode ? "app-dark" : "app-light"} ${isAuthenticated ? "authenticated-shell" : "public-shell"} ${sidebarCollapsed ? "sidebar-is-collapsed" : ""}`}
        >
          <BrandCanvas />
          {isAuthenticated && (
            <SidebarSection
              collapsed={sidebarCollapsed}
              onToggle={handleSidebarCollapse}
            />
          )}
          <Layout>
            <HeaderSection />
            <MainSection />
          </Layout>
          {isAuthenticated && <ChatSection themeMode={themeMode.themeMode} />}
        </div>
      </Router>
    </ConfigProvider>
  );
};

export default App;
